import React from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Link,
  Typography,
  Grid2,
  useMediaQuery,
  Theme,
  Divider,
  styled,
} from "@mui/material";
import { useMandatorContext } from "@rentablo/theming";
import { useFeatureFlag } from "@hooks";
import { Disclaimer, TermsMainTitle, TermsText, TermsTitle } from "@components";
import { PRIVACY } from "@helpers/urlConstants";

const NAMESPACE = "termsOfUsePage";

const StyledDivider = styled(Divider)(({ theme }) => ({
  paddingTop: theme.spacing(5),
  [theme.breakpoints.down("md")]: {
    paddingTop: theme.spacing(1),
  },
}));

export const TermsOfUse = () => {
  const { t, i18n } = useTranslation("translation", { keyPrefix: NAMESPACE });
  const { t: tGlobal } = useTranslation();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );
  const isCashbackEnabled = useFeatureFlag("CASHBACK_WIZARD");
  const { isRentablo } = useMandatorContext();
  const mandatorShortName = isRentablo ? "Rentablo" : "Depotcheck";
  const supportEmail = isRentablo ? "info@rentablo.de" : "info@wealthapi.eu";
  const vars = { mandatorShortName, supportEmail };
  // The referral and payout sections precede every section cross-referenced
  // below; the billing one comes after all of them.
  const cashbackOffset = isCashbackEnabled ? 2 : 0;
  let sectionNumber = 0;

  return (
    <Grid2 container>
      <Grid2
        size={{
          xs: 12,
          lg: 8,
        }}
      >
        <TermsMainTitle variant="h1" mb={3} sx={{ hyphens: "auto" }}>
          {t("title")}
        </TermsMainTitle>
        <TermsText variant="body3" color="textSecondary">
          {t("introText1")}
        </TermsText>
        <TermsText variant="body3">
          {isRentablo ? "Rentablo GmbH" : tGlobal("aboutCompany.company.name")}{" "}
          {t("introCompanyNote", vars)}
          <br />
          {tGlobal("aboutCompany.company.street")}
          {tGlobal("aboutCompany.company.streetNumber")}
          <br />
          {tGlobal("aboutCompany.company.zip")}{" "}
          {tGlobal("aboutCompany.company.city")}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("introText2", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("introText3", vars)}
        </TermsText>
        <Typography variant="h2" color="textPrimary" mt={isMobile ? 20 : 30}>
          {t("generalTitle")}
        </Typography>
        <Typography variant="h4" color="textPrimary" my={6}>
          {(sectionNumber += 1)}. {t("section1.title")}
        </Typography>
        <TermsText variant="body3" color="textSecondary">
          {t("section1.p1", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section1.p2", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section1.p3", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section1.p4", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section1.p5", vars)}
        </TermsText>
        <StyledDivider />
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section2.title")}
        </TermsTitle>
        <TermsText variant="body3" color="textSecondary">
          <Trans
            i18nKey={`${NAMESPACE}.section2.text`}
            values={vars}
            components={[
              <Link href={`${import.meta.env.VITE_APP_V2_URL}${PRIVACY}`} />,
            ]}
          />
        </TermsText>
        <StyledDivider />
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section3.title")}
        </TermsTitle>
        <TermsText variant="body3" color="textSecondary">
          {t("section3.p1")}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section3.p2")}
        </TermsText>
        <StyledDivider />
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section4.title")}
        </TermsTitle>
        <TermsText variant="body3" color="textSecondary">
          {t("section4.p1", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section4.p2", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section4.p3")}
        </TermsText>
        <StyledDivider />
        {isCashbackEnabled && (
          <>
            <TermsTitle variant="h4" color="textPrimary">
              {(sectionNumber += 1)}. {t("sectionCashbackReferral.title")}
            </TermsTitle>
            <Typography component="div" variant="body3" color="textSecondary">
              {t("sectionCashbackReferral.p1")}
              <ul>
                <li>{t("sectionCashbackReferral.p1Item1")}</li>
                <li>{t("sectionCashbackReferral.p1Item2")}</li>
                <li>{t("sectionCashbackReferral.p1Item3")}</li>
                <li>{t("sectionCashbackReferral.p1Item4")}</li>
              </ul>
            </Typography>
            <TermsText variant="body3" color="textSecondary">
              {t("sectionCashbackReferral.p2")}
            </TermsText>
            <TermsText variant="body3" color="textSecondary">
              {t("sectionCashbackReferral.p3")}
            </TermsText>
            <TermsText variant="body3" color="textSecondary">
              {t("sectionCashbackReferral.p4", vars)}
            </TermsText>
            <StyledDivider />
          </>
        )}
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section5.title")}
        </TermsTitle>
        <TermsText variant="body3" color="textSecondary">
          {t("section5.text", vars)}
        </TermsText>
        <StyledDivider />
        {isCashbackEnabled && (
          <>
            <TermsTitle variant="h4" color="textPrimary">
              {(sectionNumber += 1)}. {t("sectionCashbackPayout.title")}
            </TermsTitle>
            <TermsText variant="body3" color="textSecondary">
              {t("sectionCashbackPayout.p1", vars)}
            </TermsText>
            <TermsText variant="body3" color="textSecondary">
              {t("sectionCashbackPayout.p2", vars)}
            </TermsText>
            <TermsText variant="body3" color="textSecondary">
              {t("sectionCashbackPayout.p2ItemA", vars)}
            </TermsText>
            <TermsText variant="body3" color="textSecondary">
              {t("sectionCashbackPayout.p2ItemB", vars)}
            </TermsText>
            <TermsText variant="body3" color="textSecondary">
              {t("sectionCashbackPayout.p3", vars)}
            </TermsText>
            <TermsText variant="body3" color="textSecondary">
              {t("sectionCashbackPayout.p3ItemA", vars)}
            </TermsText>
            <TermsText variant="body3" color="textSecondary">
              {t("sectionCashbackPayout.p3ItemB", vars)}
            </TermsText>
            <StyledDivider />
          </>
        )}
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section6.title")}
        </TermsTitle>
        <TermsText variant="body3" color="textSecondary">
          {t("section6.p1", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section6.p2", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section6.p3", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section6.p4", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section6.p5", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section6.p6", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section6.p7", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section6.p8", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section6.p9", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section6.p10", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section6.p11", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section6.p12", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section6.p13", vars)}
        </TermsText>
        <StyledDivider />
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section7.title")}
        </TermsTitle>
        <TermsText variant="body3" color="textSecondary">
          {t("section7.p1", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section7.p2")}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section7.p3", vars)}
        </TermsText>
        <StyledDivider />
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section8.title")}
        </TermsTitle>
        <TermsText variant="body3" color="textSecondary">
          {t("section8.p1", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section8.p2", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section8.p3", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section8.p4", vars)}
        </TermsText>
        <StyledDivider />
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section9.title")}
        </TermsTitle>
        <TermsText variant="body3" color="textSecondary">
          {t("section9.p1")}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section9.p2", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section9.p3", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section9.p4", {
            ...vars,
            usageRightsSection: 8 + cashbackOffset,
            obligationsSection: 10 + cashbackOffset,
          })}
        </TermsText>
        <StyledDivider />
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section10.title")}
        </TermsTitle>
        <TermsText variant="body3" color="textSecondary">
          {t("section10.p1", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section10.p2", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section10.p3", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section10.p4", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section10.p5", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section10.p6", vars)}
        </TermsText>
        <StyledDivider />
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section11.title")}
        </TermsTitle>
        <TermsText variant="body3" color="textSecondary">
          {t("section11.p1", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section11.p2", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section11.p3", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section11.p4", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section11.p5", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section11.p6", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section11.p7", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section11.p8", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section11.p9", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section11.p10")}
        </TermsText>
        <StyledDivider />
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section12.title")}
        </TermsTitle>
        <TermsText variant="body3" color="textSecondary">
          {t("section12.p1", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section12.p2", vars)}
        </TermsText>
        <StyledDivider />
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section13.title")}
        </TermsTitle>
        <Disclaimer showTitle={false} gridItemLg={12} />
        <StyledDivider />
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section14.title")}
        </TermsTitle>
        <TermsText variant="body3" color="textSecondary">
          {t("section14.p1", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section14.p2", vars)}
        </TermsText>
        <StyledDivider />
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section15.title")}
        </TermsTitle>
        <TermsText variant="body3" color="textSecondary">
          {t("section15.p1", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section15.p2", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section15.p3")}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section15.p4")}
        </TermsText>
        <StyledDivider />
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section16.title")}
        </TermsTitle>
        <TermsText variant="body3" color="textSecondary">
          {t("section16.p1", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section16.p2", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section16.p3", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section16.p4", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section16.p5", vars)}
        </TermsText>
        <StyledDivider />
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section17.title")}
        </TermsTitle>
        <TermsText variant="body3" color="textSecondary">
          {t("section17.p1")}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {isRentablo ? "Rentablo GmbH" : tGlobal("aboutCompany.company.name")}{" "}
          <br />
          {tGlobal("aboutCompany.company.street")}{" "}
          {tGlobal("aboutCompany.company.streetNumber")}
          <br />
          {tGlobal("aboutCompany.company.zip")}{" "}
          {tGlobal("aboutCompany.company.city")}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section17.p2")}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section17.p3")}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section17.p4")}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section17.p5", vars)}
        </TermsText>
        <StyledDivider />
        {isCashbackEnabled && (
          <>
            <TermsTitle variant="h4" color="textPrimary">
              {(sectionNumber += 1)}. {t("sectionCashbackBilling.title")}
            </TermsTitle>
            <TermsText variant="body3" color="textSecondary">
              {isRentablo
                ? t("sectionCashbackBilling.p1Rentablo", vars)
                : t("sectionCashbackBilling.p1", vars)}
            </TermsText>
            <TermsText variant="body3" color="textSecondary">
              {t("sectionCashbackBilling.p2")}
            </TermsText>
            <TermsText variant="body3" color="textSecondary">
              {t("sectionCashbackBilling.p3")}
            </TermsText>
            <TermsText variant="body3" color="textSecondary">
              {t("sectionCashbackBilling.p4")}
            </TermsText>
            <TermsText variant="body3" color="textSecondary">
              {t("sectionCashbackBilling.p5")}
            </TermsText>
            <TermsText variant="body3" color="textSecondary">
              {t("sectionCashbackBilling.p6", vars)}
            </TermsText>
            <StyledDivider />
          </>
        )}
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section18.title")}
        </TermsTitle>
        {isCashbackEnabled && (
          <TermsText variant="body3" color="textSecondary">
            {t("section18.cashbackP1Title")}
            <br />
            {t("section18.cashbackP1Text", vars)}
          </TermsText>
        )}
        <TermsText variant="body3" color="textSecondary">
          {t("section18.p2Title", { ...vars, num: isCashbackEnabled ? 2 : 1 })}
          <br />
          {t("section18.p2Text", vars)}
        </TermsText>
        {isRentablo && (
          <>
            <TermsText variant="body3" color="textSecondary">
              {t("section18.p3Title", vars)}
              <br />
              {t("section18.p3Text", vars)}
            </TermsText>
            <TermsText variant="body3" color="textSecondary">
              {t("section18.p4Title", vars)}
              <br />
              {t("section18.p4Text")}
            </TermsText>
          </>
        )}

        <StyledDivider />
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section19.title")}
        </TermsTitle>
        <TermsText variant="body3" color="textSecondary">
          {t("section19.p1", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section19.p2", { addressSection: 17 + cashbackOffset })}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section19.p3", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section19.p4")}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          <Trans
            i18nKey={`${NAMESPACE}.section19.p5`}
            values={vars}
            components={[
              <Link href={`mailto:${supportEmail}`} underline="hover" />,
            ]}
          />
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section19.p6", vars)}
        </TermsText>
        <StyledDivider />
        <TermsTitle variant="h4" color="textPrimary">
          {(sectionNumber += 1)}. {t("section20.title")}
        </TermsTitle>
        <TermsText variant="body3" color="textSecondary">
          {t("section20.p1")}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section20.p2")}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section20.p3")}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("section20.p4", vars)}
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          <Trans
            i18nKey={`${NAMESPACE}.published`}
            values={{ rootUrl: import.meta.env.VITE_APP_ROOT_URL }}
            components={[
              <Link
                href={import.meta.env.VITE_APP_ROOT_URL}
                underline="hover"
              />,
            ]}
          />
        </TermsText>
        <TermsText variant="body3" color="textSecondary">
          {t("date")}
        </TermsText>
        {i18n.language === "en" && (
          <TermsText variant="body3" color="textSecondary" mt={10}>
            {tGlobal("common.englishLegalNote")}
          </TermsText>
        )}
      </Grid2>
    </Grid2>
  );
};
